import { defineStore, storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import {
    AuthStatus,
    useAuthenticationService,
} from "@/composables/authentication";
import { useToast } from "primevue/usetoast";
import { useServices, types } from "@/lib/openapi";
import { useOrganizationManager } from "@/composables/organization-manager";

export type Stek = types.Stek;

export const useSteksStore = defineStore("steks", () => {
    const toast = useToast();
    const isLoading = ref(false);
    const steks = ref<Stek[]>();
    const { organization } = useOrganizationManager();
    const { authStatus } = useAuthenticationService();
    const api = useServices({
        requires: {
            auth: true,
            organization: true,
            stek: false,
        },
    });

    const controller = ref(new AbortController());

    const getSteks = async () => {
        controller.value.abort();
        controller.value = new AbortController();

        isLoading.value = true;
        const { response, data } =
            await api.startStekStekMgtGetSteksByOrganizationStartPost({
                body: {
                    signal: controller.value.signal,
                },
            });

        if (response.ok) {
            steks.value = data;
        } else {
            if (response.status === 0) {
                // Request was aborted by the AbortController
                return;
            }
            throw new Error(`Request failed with status ${response.status}`);
        }
        isLoading.value = false;
    };

    // Cannot get steks if we aren't signed in.
    watch(
        authStatus,
        (newAuthStatus) => {
            if (newAuthStatus === AuthStatus.SIGNED_IN) {
                getSteks();
            }
        },
        { immediate: true }
    );

    watch(
        () => organization.value?.identity,
        async (identity) => {
            if (identity) {
                await getSteks();
            } else {
                steks.value = undefined;
            }
        }
    );

    /**
     * Create a new Stek.
     */
    const create = async (
        name: string,
        opts: { showToast: boolean } = { showToast: true }
    ) => {
        isLoading.value = true;
        const { response } = await api.startStekStekMgtCreateStekStartPost({
            body: { name },
        });

        if (response.ok) {
            await getSteks();
        } else if (opts.showToast) {
            toast.add({
                severity: "error",
                summary: "Could not create stek",
                detail: "Server returned " + response.status,
            });
        }
        isLoading.value = false;
    };

    return {
        create,
        getSteks,
        steks,
        isLoading,
        stekNames: computed(() =>
            (steks.value || [])
                .map((o) => o.name)
                .sort((a, b) =>
                    a.localeCompare(b, undefined, { "sensitivity": "base" })
                )
        ),
    };
});

export const useSteksManager = () => storeToRefs(useSteksStore());
